import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface HostCandidate {
  id: string;
  first_name: string | null;
  last_name: string | null;
  member_type: string | null;
}

/**
 * Host pickers only list profiles with member_type = 'host'. Artists who only
 * attend events are deliberately excluded — hosting is the qualifying role.
 * The member base is ~2k profiles, which blew past PostgREST's 1000-row ceiling
 * and silently truncated the list at "L". Members who occasionally host should be
 * upgraded to host status in the admin user editor instead of widening this filter.
 *
 * `includeIds` keeps legacy hosts (saved before they were tagged) visible when
 * editing an existing event.
 */
export const useHostCandidates = (includeIds: (string | null | undefined)[] = []) => {
  const extraIds = Array.from(
    new Set(
      includeIds.filter(
        (id): id is string =>
          !!id && id !== "nimi" && id !== "member" && id !== "museum"
      )
    )
  ).sort();

  return useQuery({
    queryKey: ["host-candidates", extraIds],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<HostCandidate[]> => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, first_name, last_name, member_type")
        .eq("member_type", "host")
        .order("first_name", { ascending: true })
        .order("last_name", { ascending: true });

      if (error) throw error;

      const rows: HostCandidate[] = data ?? [];
      const missing = extraIds.filter((id) => !rows.some((r) => r.id === id));

      if (missing.length > 0) {
        const { data: legacy, error: legacyError } = await supabase
          .from("profiles")
          .select("id, first_name, last_name, member_type")
          .in("id", missing);

        if (legacyError) throw legacyError;
        rows.push(...(legacy ?? []));
      }

      return rows.sort((a, b) =>
        `${a.first_name ?? ""} ${a.last_name ?? ""}`
          .trim()
          .localeCompare(`${b.first_name ?? ""} ${b.last_name ?? ""}`.trim())
      );
    },
  });
};
